// GET <resource>/<id>/assets
import { ApiDatumResponse, ApiDataResponse, ApiResponseObject } from '@api/v4/ApiResponseTypes';

export type AssetsResponse = ApiDataResponse<Asset>;

export type AssetResponse = ApiDatumResponse<Asset>;

export type AssetsResponseObject = ApiResponseObject<Asset>;

export type AssetData = ColorData | FontData | PersonData | PressData | ExternalMediumData | GenericFileData;

export interface Asset {
  description: string;
  name: string;
  thumbnail_url: string | null;
  approved?: boolean;
  best_link_for?: string;
  created_at?: string;
  data?: AssetData;
  extension?: string;
  score?: number;
  thumbnail_override?: LinkObject;
  updated_at?: string;
  watermark_url?: string;
  watermark?: LinkObject;
}

export enum AssetTypes {
  GenericFile = "generic_files",
  Color = "colors",
  People = "people",
  Press = "press",
  Text = "texts",
  Font = "fonts",
  ExternalMedium = "external_media",
}

export interface LinkObject {
  url?: string;
  filename?: string;
}

export interface GenericFileData {
  three_sixty_viewer?: boolean;
  asset_background_color?: string;
}

export interface ColorData {
  c: string;
  m: string;
  y: string;
  k: string;
  r: string;
  g: string;
  b: string;
  hex: string;
  pantone: string;
  pantone_u: string;
}

export interface ExternalMediumData {
  url: string;
}

export interface PersonData {
  title: string;
  email: string;
  phone_number: string;
  linkedin: string;
  twitter: string;
}
export interface FontData {
  other_font_id: string;
  web_font_id: string;
  web_font_type: string;
}

export interface PressData {
  url: string;
  published_date?: string;
}

export interface Tag {
  name: string;
  auto_generated: boolean;
}

/* Type predicates */
const isGenericFileData = (assetType: AssetTypes, data: AssetData): data is GenericFileData => (
  assetType === AssetTypes.GenericFile
);

const isColorData = (assetType: AssetTypes, data: AssetData): data is ColorData => (
  assetType === AssetTypes.Color
);

const isFontData = (assetType: AssetTypes, data: AssetData): data is FontData => (
  assetType === AssetTypes.Font
);

const isPersonData = (assetType: AssetTypes, data: AssetData): data is PersonData => (
  assetType === AssetTypes.People
);

const isPressData = (assetType: AssetTypes, data: AssetData): data is PressData => (
  assetType === AssetTypes.Press
);

const isExternalMediumData = (assetType: AssetTypes, data: AssetData): data is ExternalMediumData => (
  assetType === AssetTypes.ExternalMedium
);

const typePredicateMap = {
  [AssetTypes.GenericFile]: isGenericFileData,
  [AssetTypes.Color]: isColorData,
  [AssetTypes.ExternalMedium]: isExternalMediumData,
  [AssetTypes.Font]: isFontData,
  [AssetTypes.People]: isPersonData,
  [AssetTypes.Press]: isPressData,
};

export const isExpectedDataShape = <T extends AssetData>(assetType: AssetTypes, expectedAssetType: AssetTypes, data: AssetData): data is T => (
  typePredicateMap[expectedAssetType](assetType, data)
);
